import React from "react";
import { Drawer, Box, IconButton, Typography, Badge } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";

import Colors from "../../constants/Colors";
import { ReactComponent as Logo } from "../../assets/imgs/logo.svg";
import NavigationItems from "../UI/menu/navigationItems/NavigationItems";
import { versionNumber } from "../../constants/Env";
import SideBarFooter from "../sidebarFooter/SidebarFooter";
import UserProfile from "../sidebar/UserProfile";
import { ScrollNavItens } from "../UI/generalLayout/Layout.styled";

interface IMobileSidebar {
  isMobileSidebarToggled: boolean;
  toggleMobileSidebar: () => void;
  currentRoute: string;
}

const MobileSidebar = ({
  toggleMobileSidebar,
  isMobileSidebarToggled,
  currentRoute,
}: IMobileSidebar) => {
  return (
    <>
      {/* Mobile Top App Bar */}
      <Box
        sx={{
          height: 60,
          backgroundColor: Colors.SPM600,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
          position: "sticky",
          top: 0,
          zIndex: 1100,
        }}
      >
        {/* Left: Menu Toggle Button */}
        <IconButton
          onClick={toggleMobileSidebar}
          size="medium"
          sx={{ color: "white", p: 1 }}
        >
          <MenuIcon sx={{ fontSize: 28 }} />
        </IconButton>

        {/* Center: Hospital Logo */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Logo style={{ maxHeight: 34, width: "auto" }} />
        </Box>

        {/* Right: Notifications Button */}
        <IconButton size="medium" sx={{ color: "white", p: 1 }}>
          <Badge color="warning" variant="dot">
            <NotificationsNoneIcon sx={{ fontSize: 26 }} />
          </Badge>
        </IconButton>
      </Box>

      {/* Modern Slide-out Mobile Navigation Drawer */}
      <Drawer
        anchor="left"
        open={isMobileSidebarToggled}
        onClose={toggleMobileSidebar}
        PaperProps={{
          sx: {
            width: "82vw",
            maxWidth: 320,
            backgroundColor: Colors.SPM600,
            color: "white",
            display: "flex",
            flexDirection: "column",
            boxShadow: "4px 0 20px rgba(0,0,0,0.3)",
          },
        }}
      >
        {/* Mobile Drawer Top Header: Logo + Close Button */}
        <Box
          sx={{
            pt: 2,
            pb: 1.5,
            px: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
            <Logo style={{ maxHeight: 38, width: "auto" }} />
          </Box>

          <IconButton
            onClick={toggleMobileSidebar}
            size="small"
            sx={{
              color: "white",
              p: 0.8,
              borderRadius: "8px",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.2)",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 22 }} />
          </IconButton>
        </Box>

        {/* Mobile User Profile & Workspace Switchers (Company & Role Selectors) */}
        <UserProfile isCollapsed={false} />

        {/* Scrollable Navigation Menu */}
        <ScrollNavItens isMobile={true} style={{ flex: 1, overflowY: "auto", paddingTop: "4px" }}>
          <NavigationItems currentRoute={currentRoute} />
        </ScrollNavItens>

        {/* Mobile Footer: Logout + Version Number */}
        <Box
          sx={{
            pb: 2,
            pt: 1,
            px: 2,
            width: "100%",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            borderTop: "1px solid rgba(255, 255, 255, 0.12)",
          }}
        >
          <SideBarFooter isCollapsed={false} />

          <Typography
            variant="caption"
            sx={{
              display: "block",
              textAlign: "center",
              color: "rgba(255, 255, 255, 0.55)",
              fontSize: "0.7rem",
              mt: 0.2,
              fontFamily: "Inter, sans-serif",
            }}
          >
            v{versionNumber}
          </Typography>
        </Box>
      </Drawer>
    </>
  );
};

export default MobileSidebar;